// ./src/utils/api/VolunteerRoleService.ts
//
// VolunteerRole (account-level reusable volunteer roles) service. OpResult-native.
// Account-owned: every read is scoped to the caller's account and only the
// caller's own roles are editable (see the `rights` field). A multi-id
// update/remove is a bulk op (server restricts a bulk update to status).
// See em-scaffold-ui.
import { gql } from "@apollo/client";
import { OpResult } from "@sdflc/api-helpers";

import { ApiService } from "./ApiService";
import { listAllPages, ListAllOptions } from "./listAllPages";
import { Pagination, SortItem } from "./commonApiTypes";

// =============================================================================
// GraphQL
// =============================================================================

export const VOLUNTEER_ROLE_FIELDS = `
  id
  accountId
  name
  summary
  responsibilities
  eligibility
  color
  icon
  hourlyRate
  status
  rights
  createdAt
  updatedAt
`;

const VOLUNTEER_ROLE_LIST_QUERY = gql`
  query VolunteerRoleList($filter: VolunteerRoleFilter, $params: PaginationAndSorting) {
    volunteerRoleList(filter: $filter, params: $params) {
      code
      data { ${VOLUNTEER_ROLE_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const VOLUNTEER_ROLE_GET_QUERY = gql`
  query VolunteerRoleGet($id: ID) {
    volunteerRoleGet(id: $id) {
      code
      data { ${VOLUNTEER_ROLE_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const VOLUNTEER_ROLE_GET_MANY_QUERY = gql`
  query VolunteerRoleGetMany($ids: [ID]) {
    volunteerRoleGetMany(ids: $ids) {
      code
      data { ${VOLUNTEER_ROLE_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const VOLUNTEER_ROLE_CREATE_MUTATION = gql`
  mutation VolunteerRoleCreate($params: VolunteerRoleInput) {
    volunteerRoleCreate(params: $params) {
      code
      data { ${VOLUNTEER_ROLE_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const VOLUNTEER_ROLE_UPDATE_MUTATION = gql`
  mutation VolunteerRoleUpdate($where: VolunteerRoleWhereInput, $params: VolunteerRoleInput) {
    volunteerRoleUpdate(where: $where, params: $params) {
      code
      data { ${VOLUNTEER_ROLE_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const VOLUNTEER_ROLE_REMOVE_MUTATION = gql`
  mutation VolunteerRoleRemove($where: VolunteerRoleWhereInput) {
    volunteerRoleRemove(where: $where) {
      code
      data { id }
      errors { name errors warnings }
    }
  }
`;

// =============================================================================
// Types (match the eventsmagic-api VolunteerRole schema)
// =============================================================================

export interface VolunteerRole {
  id?: string;
  accountId?: string | null;
  /** Role name, e.g. "Route Marshal". */
  name?: string | null;
  /** Short one-line summary shown in lists. */
  summary?: string | null;
  /** What the role does (markdown). */
  responsibilities?: string | null;
  /** Who qualifies — age/skills/certifications (markdown). */
  eligibility?: string | null;
  /** UI badge color. */
  color?: string | null;
  /** Registered icon name. */
  icon?: string | null;
  /** #A4.7 — what the role pays per hour, in each EVENT's currency; null = an unpaid volunteer. */
  hourlyRate?: number | null;
  status?: number;
  /** Actions the current caller may perform ('edit' | 'remove' | 'duplicate'). Empty = read-only. */
  rights?: string[] | null;
  createdAt?: string;
  updatedAt?: string;
}

export interface VolunteerRoleInput {
  name?: string | null;
  summary?: string | null;
  responsibilities?: string | null;
  eligibility?: string | null;
  color?: string | null;
  icon?: string | null;
  /** #A4.7 — null = unpaid. */
  hourlyRate?: number | null;
  status?: number | null;
}

// A bulk update (>1 id) may only mass-change status (server-enforced).
export interface VolunteerRoleBulkInput {
  status?: number | null;
}

export interface VolunteerRoleFilter {
  id?: string[] | null;
  status?: number[] | null;
  searchKeyword?: string | null;
}

export interface VolunteerRoleWhereInput {
  /** One id, or many for a bulk update/remove. */
  id?: string | string[];
}

// =============================================================================
// Service
// =============================================================================

export class VolunteerRoleService {
  static async list(
    filter?: VolunteerRoleFilter,
    pagination?: Pagination,
    sortBy?: SortItem[]
  ): Promise<OpResult> {
    return ApiService.executeQuery(VOLUNTEER_ROLE_LIST_QUERY, {
      filter: filter || {},
      params: { pagination: pagination || undefined, sortBy: sortBy || undefined },
    });
  }

  /**
   * Every row matching `filter`, paged — for PRINT SHEETS and exports, which need the whole set.
   * ⚠ Never pass a literal `pageSize` in a print page instead of this: paper has no Load-more button,
   * so a capped sheet is indistinguishable from a complete one. See `listAllPages`.
   */
  static async listAll(filter?: VolunteerRoleFilter, sortBy?: SortItem[], opts?: ListAllOptions): Promise<OpResult> {
    return listAllPages<VolunteerRole>(VolunteerRoleService.list, filter, sortBy, opts);
  }

  static async get(id: string): Promise<OpResult> {
    return ApiService.executeQuery(VOLUNTEER_ROLE_GET_QUERY, { id });
  }

  static async getMany(ids: string[]): Promise<OpResult> {
    return ApiService.executeQuery(VOLUNTEER_ROLE_GET_MANY_QUERY, { ids });
  }

  static async create(params: VolunteerRoleInput): Promise<OpResult> {
    return ApiService.executeMutation(VOLUNTEER_ROLE_CREATE_MUTATION, { params });
  }

  static async update(where: VolunteerRoleWhereInput, params: VolunteerRoleInput): Promise<OpResult> {
    return ApiService.executeMutation(VOLUNTEER_ROLE_UPDATE_MUTATION, { where, params });
  }

  static async remove(id: string): Promise<OpResult> {
    return ApiService.executeMutation(VOLUNTEER_ROLE_REMOVE_MUTATION, { where: { id } });
  }

  /** Mass-change status on every selected role in one call (server restricts a multi-id update to status). */
  static async bulkUpdate(ids: string[], params: VolunteerRoleBulkInput): Promise<OpResult> {
    if (ids.length === 0) {
      return new OpResult();
    }

    return ApiService.executeMutation(VOLUNTEER_ROLE_UPDATE_MUTATION, { where: { id: ids }, params });
  }

  /** Remove every selected role in one call. */
  static async removeMany(ids: string[]): Promise<OpResult> {
    if (ids.length === 0) {
      return new OpResult();
    }

    return ApiService.executeMutation(VOLUNTEER_ROLE_REMOVE_MUTATION, { where: { id: ids } });
  }
}
